// One place that turns a Firebase Auth failure into something a student or
// parent can act on. A real student on production saw "Firebase: Error
// (auth/too-many-requests)." after tapping resend on the verification screen
// (2026-10-06): no Firebase wording, no error codes, ever, in production.
//
// Codes are read by duck typing (err.code, or "(auth/...)" inside the
// message) rather than `instanceof FirebaseError`, so this also catches an
// error that was re-wrapped, came from a second copy of the SDK, or was
// mocked in a test.

export type AuthErrorContext
  = | 'sign-in'
    | 'sign-up'
    | 'google'
    | 'resend-verification'
    | 'verify-email'
    | 'password-reset-request'
    | 'password-reset'
    | 'change-password'
    | 'change-email'
    | 'reauth'
    | 'link'
    | 'session'
    | 'generic';

// Mirrors EMAIL.general in @repo/brand (not a dependency of this package).
const CONTACT_EMAIL = 'info@elitescholars.co.za';

export const GENERIC_AUTH_ERROR = 'Something went wrong. Please try again in a moment.';

const OUR_SIDE = `Something went wrong on our side. Please try again. If it keeps happening, contact us at ${ CONTACT_EMAIL }.`;

const NETWORK = 'We could not reach our servers. Please check your internet connection and try again.';

const SESSION_ENDED = 'You have been signed out. Please log in again to carry on.';

const WRONG_CREDENTIALS = 'Your email or password is incorrect. Please check them and try again.';

const RECENT_LOGIN = 'For your safety, please log out, log in again, and then try this once more.';

const LINK_EXPIRED: Partial<Record<AuthErrorContext, string>> = {
  'verify-email': 'This verification link has expired. Please log in and ask for a new verification email.',
  'password-reset': 'This password reset link has expired. Please go to "Forgot password" and ask for a new one.'
};

const LINK_INVALID: Partial<Record<AuthErrorContext, string>> = {
  'verify-email': 'This verification link has already been used or is no longer valid. If you have already verified your email, just log in.',
  'password-reset': 'This password reset link has already been used or is no longer valid. Please go to "Forgot password" and ask for a new one.'
};

const TOO_MANY: Partial<Record<AuthErrorContext, string>> = {
  'resend-verification': 'We\'ve already sent you a verification email. Please wait a few minutes before asking for another one, and check your Junk or Spam folder.',
  'password-reset-request': 'We\'ve already sent you a password reset email. Please wait a few minutes before asking for another one, and check your Junk or Spam folder.',
  'sign-in': 'Too many sign-in attempts. Please wait a few minutes and try again, or reset your password if you have forgotten it.',
  'google': 'Too many sign-in attempts. Please wait a few minutes and try again.',
  'reauth': 'Too many attempts. Please wait a few minutes and try again, or reset your password if you have forgotten it.'
};

// A message of '' means "say nothing": the person closed the Google window
// themselves, so there is nothing to explain. Every error alert in the apps
// renders nothing for an empty message.
function messageFor(code: string, context: AuthErrorContext): string | null {

  switch (code) {

    case 'auth/too-many-requests': {

      return TOO_MANY[context] ?? 'Too many attempts. Please wait a few minutes and try again.';
    }

    case 'auth/network-request-failed':

    case 'auth/timeout': {

      return NETWORK;
    }

    case 'auth/user-not-found':
    case 'auth/wrong-password':
    case 'auth/invalid-credential':

    case 'auth/invalid-login-credentials': {

      // One answer for all four, so nobody can use the form to find out which
      // email addresses have accounts.
      if (context === 'reauth' || context === 'change-password' || context === 'change-email') {

        return 'That password is incorrect. Please try again.';
      }

      if (context === 'session') {

        return SESSION_ENDED;
      }

      return WRONG_CREDENTIALS;
    }

    case 'auth/missing-password': {

      return 'Please enter your password.';
    }

    case 'auth/email-already-in-use': {

      if (context === 'change-email' || context === 'link') {

        return 'Another account already uses this email address. Please use a different one.';
      }

      return 'An account with this email already exists. Please log in instead, or reset your password if you have forgotten it.';
    }

    case 'auth/weak-password': {

      return 'That password is too easy to guess. Please choose a longer one that mixes letters, numbers and symbols.';
    }

    case 'auth/invalid-email': {

      return 'That email address does not look right. Please check it and try again.';
    }

    case 'auth/missing-email': {

      return 'Please enter your email address.';
    }

    case 'auth/user-disabled': {

      return `This account has been switched off. If you think this is a mistake, contact us at ${ CONTACT_EMAIL }.`;
    }

    case 'auth/expired-action-code': {

      return LINK_EXPIRED[context] ?? 'This link has expired. Please ask for a new one.';
    }

    case 'auth/invalid-action-code': {

      return LINK_INVALID[context] ?? 'This link has already been used or is no longer valid. Please ask for a new one.';
    }

    case 'auth/requires-recent-login': {

      return RECENT_LOGIN;
    }

    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':

    case 'auth/user-cancelled': {

      return '';
    }

    case 'auth/popup-blocked': {

      return 'Your browser blocked the Google sign-in window. Please allow pop-ups for this site and try again.';
    }

    case 'auth/account-exists-with-different-credential': {

      return 'You already have an account with this email. Please log in the way you first signed up (for example with your email and password).';
    }

    case 'auth/credential-already-in-use': {

      return 'That sign-in method is already linked to a different Elite Scholars account.';
    }

    case 'auth/provider-already-linked': {

      return 'That sign-in method is already linked to your account.';
    }

    case 'auth/user-mismatch': {

      return 'That is a different account from the one you are signed in with. Please use the same account.';
    }

    case 'auth/user-token-expired':

    case 'auth/invalid-user-token': {

      return SESSION_ENDED;
    }

    case 'auth/quota-exceeded': {

      return 'We are very busy right now. Please wait a few minutes and try again.';
    }

    case 'auth/web-storage-unsupported':

    case 'auth/operation-not-supported-in-this-environment': {

      return 'Your browser is blocking what we need to sign you in. Please turn off private browsing or allow cookies for this site, then try again.';
    }

    case 'auth/invalid-phone-number': {

      return 'That phone number does not look right. Please check it and try again.';
    }

    case 'auth/phone-number-already-exists': {

      return 'That phone number is already used by another account.';
    }

    case 'auth/unauthorized-domain':
    case 'auth/operation-not-allowed':
    case 'auth/internal-error':
    case 'auth/invalid-api-key':
    case 'auth/app-not-authorized':
    case 'auth/app-deleted':
    case 'auth/invalid-continue-uri':
    case 'auth/unauthorized-continue-uri':
    case 'auth/missing-continue-uri':

    case 'auth/firebase-app-check-token-is-invalid': {

      return OUR_SIDE;
    }

    default: {

      // App Check codes vary by SDK version (app-check/..., auth/...app-check...).
      if (code.includes('app-check')) {

        return OUR_SIDE;
      }

      return null;
    }
  }
}

// "Firebase: Error (auth/too-many-requests)." or a bare "auth/xyz".
const CODE_IN_PARENS = /\(((?:auth|app-check)\/[a-z0-9-]+)\)/i;
const BARE_CODE = /^\s*((?:auth|app-check)\/[a-z0-9-]+)\s*$/i;

function codeFromText(text: string): string | null {

  const match = CODE_IN_PARENS.exec(text) ?? BARE_CODE.exec(text);
  return match ? match[1]!.toLowerCase() : null;
}

/** The Firebase error code carried by `err`, if any (e.g. "auth/too-many-requests"). */
export function getAuthErrorCode(err: unknown): string | null {

  if (err && typeof err === 'object') {

    const code = (err as { code?: unknown }).code;

    if (typeof code === 'string' && /^(auth|app-check)\//.test(code)) {

      return code;
    }

    const message = (err as { message?: unknown }).message;

    if (typeof message === 'string') {

      return codeFromText(message);
    }
  }

  if (typeof err === 'string') {

    return codeFromText(err);
  }

  return null;
}

/** True for any text that must never reach a user: Firebase wording or a raw code. */
export function looksLikeFirebaseText(text: string): boolean {

  return /firebase/i.test(text) || /(?:auth|app-check)\/[a-z-]+/i.test(text) || /identitytoolkit/i.test(text);
}

function isProduction(): boolean {

  return process.env.NODE_ENV === 'production';
}

function withDevCode(message: string, code: string | null): string {

  if (isProduction() || !code || !message) {

    return message;
  }

  return `${ message } [dev: ${ code }]`;
}

/** Logs the code only: never the message, which can carry an email address. */
function logAuthError(code: string | null, context: AuthErrorContext): void {

  console.warn('[auth] request failed', {
    code: code ?? 'unknown',
    context
  });
}

/**
 * Plain-language text for any auth failure. Never returns Firebase wording or
 * an error code in production. Returns '' for a cancelled Google window.
 */
export function friendlyAuthError(err: unknown, context: AuthErrorContext = 'generic'): string {

  if (err instanceof FriendlyAuthError) {

    return err.message;
  }

  const code = getAuthErrorCode(err);
  logAuthError(code, context);
  const message = (code ? messageFor(code, context) : null) ?? GENERIC_AUTH_ERROR;
  return withDevCode(message, code);
}

/** An Error whose message is already safe to show; keeps the code for logic. */
export class FriendlyAuthError extends Error {

  readonly code: string | null;

  constructor(message: string, code: string | null) {

    super(message);
    this.name = 'FriendlyAuthError';
    this.code = code;
  }
}

/** For wrappers: `catch (err) { throw toFriendlyAuthError(err, 'sign-in'); }` */
export function toFriendlyAuthError(err: unknown, context: AuthErrorContext = 'generic'): FriendlyAuthError {

  if (err instanceof FriendlyAuthError) {

    return err;
  }

  return new FriendlyAuthError(friendlyAuthError(err, context), getAuthErrorCode(err));
}

/**
 * For catch blocks that can hold either a Firebase failure or one of our own
 * API errors (whose messages are written for users): passes our own message
 * through, and translates anything that looks like Firebase.
 */
export function userFacingError(
  err: unknown,
  fallback: string,
  context: AuthErrorContext = 'generic'
): string {

  if (err instanceof FriendlyAuthError) {

    return err.message;
  }

  if (getAuthErrorCode(err)) {

    return friendlyAuthError(err, context);
  }

  if (err instanceof Error && err.message && !looksLikeFirebaseText(err.message)) {

    return err.message;
  }

  if (err instanceof Error && err.message) {

    return friendlyAuthError(err, context);
  }

  return fallback;
}
